import type { MediaItem } from '../types/movie';
import { getPosterUrl, formatYear, formatRuntime, calculateOurRating } from '../utils/helpers';
import { GENRES, TV_GENRES } from '../utils/constants';

interface MovieCardProps {
  movie: MediaItem;
  onClick?: (movie: MediaItem) => void;
}

export function MovieCard({ movie, onClick }: MovieCardProps) {
  const year = formatYear(movie.release_date);
  const ourRating = calculateOurRating(movie.imdb_rating, movie.rt_rating);
  const hasRatings = movie.imdb_rating || movie.rt_rating || ourRating;
  const isTV = movie.media_type === 'tv';
  const genreMap = isTV ? TV_GENRES : GENRES;

  // Get genre names (first 2)
  const genreNames = movie.genre_ids
    ?.slice(0, 2)
    .map(id => genreMap[id] || GENRES[id])
    .filter(Boolean)
    .join(' / ') || '';

  return (
    <div
      className="group cursor-pointer"
      onClick={() => onClick?.(movie)}
    >
      {/* Poster with ratings overlay */}
      <div className="relative aspect-[2/3] rounded-lg overflow-hidden bg-slate-800 shadow-lg">
        {movie.poster_path ? (
          <img
            src={getPosterUrl(movie.poster_path, 'large')}
            alt={movie.title}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-700 to-slate-800">
            <div className="text-center p-4">
              <div className="text-5xl mb-2">🎬</div>
              <div className="text-slate-400 text-sm">{movie.title}</div>
            </div>
          </div>
        )}

        {/* Hover overlay */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-300" />

        {/* Ratings bar at bottom */}
        {(hasRatings || movie.vote_average > 0) && (
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/95 via-black/80 to-transparent pt-10 pb-2 px-2">
            <div className="flex justify-center gap-1.5 flex-wrap">
              {/* Our Rating */}
              {ourRating && (
                <div className="bg-gradient-to-r from-purple-600 to-violet-600 px-2 py-1 rounded-md flex items-center gap-1">
                  <span className="text-[10px] font-bold text-purple-200">Ours</span>
                  <span className="text-xs font-bold text-white">{ourRating.toFixed(1)}</span>
                </div>
              )}

              {/* IMDB Rating - fallback to vote_average if not available */}
              {movie.imdb_rating ? (
                <div className="bg-yellow-600 px-2 py-1 rounded-md flex items-center gap-1">
                  <span className="text-[10px] font-bold text-yellow-200">IMDb</span>
                  <span className="text-xs font-bold text-white">{movie.imdb_rating.toFixed(1)}</span>
                </div>
              ) : movie.vote_average > 0 && (
                <div className="bg-yellow-600 px-2 py-1 rounded-md flex items-center gap-1">
                  <span className="text-[10px] font-bold text-yellow-200">TMDB</span>
                  <span className="text-xs font-bold text-white">{movie.vote_average.toFixed(1)}</span>
                </div>
              )}

              {/* Rotten Tomatoes */}
              {movie.rt_rating && (
                <div className="bg-red-600 px-2 py-1 rounded-md flex items-center gap-1">
                  <span className="text-[10px]">🍅</span>
                  <span className="text-xs font-bold text-white">{movie.rt_rating}%</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Year badge */}
        <div className="absolute top-2 left-2 bg-black/70 text-white text-xs font-medium px-2 py-1 rounded">
          {year}
        </div>

        {/* TV indicator */}
        {isTV && (
          <div className="absolute top-2 right-2 bg-purple-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded">
            TV
          </div>
        )}
      </div>

      {/* Title and info below poster */}
      <div className="mt-2 px-1">
        <h3 className={`text-white font-medium text-sm line-clamp-2 transition-colors ${isTV ? 'group-hover:text-purple-400' : 'group-hover:text-cyan-400'}`}>
          {movie.title}
        </h3>
        <div className="flex items-center gap-1.5 mt-1 text-xs text-slate-500">
          {genreNames && <span className="truncate">{genreNames}</span>}
          {genreNames && movie.runtime && <span>•</span>}
          {movie.runtime && <span>{isTV ? `${movie.runtime}m/ep` : formatRuntime(movie.runtime)}</span>}
          {isTV && movie.number_of_seasons && (
            <>
              <span>•</span>
              <span>{movie.number_of_seasons}S</span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
